import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { ul } from './helpers';
import { EQUIPMENT_NAMES, EQUIPMENT_SECTION_NAMES } from '../Sections/BusinessOperations/Equipment/utils/form';
import {
  LOCATION_AND_PREMISES_NAMES,
  PREMISES_INFORMATION_NAMES,
} from '../Sections/BusinessOperations/LocationAndPremises/utils/form';
import { ASSETS_NAMES } from '../Sections/BusinessOperations/OtherAssets/utils/form';
import { KEY_SUPPLIERS_CHARACTERIZE, PARTNERS_NAMES } from '../Sections/BusinessOperations/PartnersSection/utils/form';
import { PRODUCTION_NAMES } from '../Sections/BusinessOperations/ProductionSection/utils/form';
import { QUALITY_NAMES } from '../Sections/BusinessOperations/QualityControl/utils/form';
import {
  STAGES_NAMES,
  TECHNOLOGICAL_PROCESS_NAMES,
} from '../Sections/BusinessOperations/TechnologicalProcess/utils/form';
import { WORKING_RESOURCES_NAMES } from '../Sections/BusinessOperations/WorkingResources/utils/form';

const PREMISES_INFORMATION_INSTRUCTIONS = {
  [PREMISES_INFORMATION_NAMES.FILE]: {
    name: '',
    instruction:
      'Include a high-quality photo of the buildings or premises. Ensure the image is well-lit and professionally taken.',
    exampleOne: '',
    exampleTwo: '',
  },
  [PREMISES_INFORMATION_NAMES.ADDRESS]: {
    name: 'Address',
    instruction:
      'Provide the full address of the buildings or premises. Include street name, building number, city, state/province, postal code, and country. Ensure the address is accurate and complete for clarity.',
    exampleOne: 'Ton Ave, Des Moines, IA 50313, US',
    exampleTwo: '03 Sylvan Ave, Mountain View, CA 94041',
  },
  [PREMISES_INFORMATION_NAMES.AREA]: {
    name: 'Area',
    instruction: 'Provide the total area of the buildings or premises in square feet, square meters, ect.',
    exampleOne: '1,2 hectare',
    exampleTwo: '750ft²',
  },
  [PREMISES_INFORMATION_NAMES.TERMS_OF_USE]: {
    name: 'Terms of use',
    instruction:
      'State whether you rent or own the premises. If renting, provide rental price details. If owned, specify current property value and purchase terms.',
    exampleOne: 'The building was purchased in January 2020 for $10 million',
    exampleTwo: 'Rent the premises for 5,000 USD per month.',
  },
  [PREMISES_INFORMATION_NAMES.CURRENT_COST]: {
    name: 'Current cost',
    instruction: 'State the current market value of the buildings or premises.',
    exampleOne: '',
    exampleTwo: '',
  },
  [PREMISES_INFORMATION_NAMES.PURPOSE]: {
    name: 'Purpose',
    instruction:
      "Describe the purpose of your building/premise usage. Specify whether it's for commercial, residential, industrial, or other purposes. Describe activities conducted within the premises, such as office work, manufacturing, retail, or services.",
    exampleOne: 'The manufacturing base',
    exampleTwo: 'Office premises in the business centre',
  },
};

const LOCATION_AND_PREMISES_INSTRUCTIONS = {
  [LOCATION_AND_PREMISES_NAMES.LOCATION]: {
    name: 'Premises Location ',
    instruction:
      'Provide a brief overview of your business`s locations or production facilities. Highlight critical geographical areas, cities, or countries where your facilities are situated. If applicable, mention the size and capacity of each facility, offering a snapshot of your operational footprint.',
    exampleOne:
      'The company`s production facility sits strategically in the heart of the Midwest, near major agricultural hubs like Des Moines, Iowa. This prime location ensures proximity to the target market and easy access to transportation networks, optimizing distribution efficiency.',
    exampleTwo:
      'The headquarters are strategically situated in the heart of Silicon Valley, enabling to tap into a rich pool of tech talent and innovation networks. The company also has satellite offices in key global hubs like London and Singapore to cater to diverse markets efficiently.',
  },
  [LOCATION_AND_PREMISES_NAMES.PREMISES_INFORMATION]: PREMISES_INFORMATION_INSTRUCTIONS,
  [LOCATION_AND_PREMISES_NAMES.QUALITIES]: {
    name: 'Location Pros/Cons',
    instruction:
      'Specify such parameters as:' +
      ul([
        'the price of rent and utility costs,',
        'proximity to target customers,',
        'proximity to suppliers,',
        'low taxes in the area,',
        'quality transportation links,',
        'available seasonal demographic changes, etc.',
      ]),
    exampleOne:
      'The company`s current location offers high rent and utility costs but provides proximity to target customers and suppliers. The area boasts low taxes, quality transportation links, and stable seasonal demographic changes.',
    exampleTwo:
      'The company`s current location offers competitive rent and utility costs, proximity to target customers in agricultural hubs, and access to suppliers. However, taxes are moderate, and seasonal demographic changes can affect labor availability.',
  },
  [LOCATION_AND_PREMISES_NAMES.FUTURE_PLANS]: {
    name: 'Future Expansion',
    instruction:
      'Delve into the specifics of the plans for new locations or production facilities. Discuss whether the company is considering constructing new buildings, leasing existing spaces, or entering partnerships for shared facilities.',
    exampleOne:
      'The company plans to expand into the Southeast, targeting emerging agricultural markets. The company will lease existing spaces initially for cost-efficiency but may explore partnerships for shared facilities later. This expansion aims to broaden the market reach and capitalize on growing demand in the region.',
    exampleTwo:
      'The company is eyeing expansion into emerging markets like Latin America and Southeast Asia, with plans to establish regional offices for better market penetration. Leasing existing spaces or entering partnerships for shared facilities are viable options, ensuring flexibility and cost-effectiveness in our expansion strategy.',
  },
};

const MAIN_EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Include a high-quality photo of the equipment. Ensure the image is well-lit and professionally taken.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Name of the group',
    instruction:
      'Combine your equipment into an equipment group. You can group based on production stages or leading equipment with auxiliary ones.',
    exampleOne: 'Assembly Line Machinery',
    exampleTwo: 'Communication Equipments',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Number of items in the group',
    instruction: 'Specify the total number of items in the equipment group.',
    exampleOne: '11',
    exampleTwo: '6',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Current Cost',
    instruction: 'State the current total value of the equipment group.',
    exampleOne: '850 000 USD',
    exampleTwo: '60 000 USD',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Purpose',
    instruction:
      'Detail the function or objective of the equipment group. Describe the outcomes achieved from using them or their role in operational processes.',
    exampleOne:
      'It is designed to automate the process of assembling components and parts, enhancing productivity and reducing labour costs. Its role is to facilitate the smooth and sequential assembly of products.',
    exampleTwo:
      'It facilitates seamless interaction among team members, clients, and stakeholders, enabling real-time collaboration and improved customer service.',
  },
};

const PURCHASED_EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Include a high-quality photo of the equipment. Ensure the image is well-lit and professionally taken.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Name of the group',
    instruction: 'Specify the name of the equipment or equipment group targeted for acquisition.',
    exampleOne: 'Material Handling Equipment',
    exampleTwo: 'Database servers',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Number of items',
    instruction: 'Specify the total number of items in the equipment group.',
    exampleOne: '13',
    exampleTwo: '20',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Planned Cost',
    instruction: 'State the planned total value of the equipment group.',
    exampleOne: '8 mln USD',
    exampleTwo: '80 000 USD',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Purpose',
    instruction:
      'Detail the function or objective of the new equipment. Describe the outcomes that will be achieved from using them or their role in operational processes.',
    exampleOne:
      'It enhances operational efficiency by reducing manual labour, and equipment ensures smooth workflow and timely delivery of components, contributing to overall productivity and cost-effectiveness.',
    exampleTwo:
      'They provide a centralized repository for storing critical information, ensuring data integrity, security, and accessibility.',
  },
};

const EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_NAMES.DESCRIPTION]: {
    name: 'Equipment Usage',
    instruction:
      'Provide a succinct overview of the equipment your business currently possesses. It includes machinery, tools, technology, and other assets crucial to your operations. Clearly state the equipment`s types, models, and capacities, offering a snapshot of the mechanisms that drive your business processes.',
    exampleOne:
      'The company`s production line is equipped with cutting-edge machinery, including CNC machining centers, robotic welding stations, and precision assembly tools. These facilities enable the fabrication of specialized components and the assembly of the tracked tractors with patented continuous track technology, ensuring top-notch quality and efficiency in production.',
    exampleTwo:
      'The company`s equipment comprises state-of-the-art translation software, including CAT tools like SDL Trados and memoQ, bolstered by high-speed servers for data processing. The company also utilizes advanced AI algorithms for quality assurance, enhancing translation accuracy and efficiency.',
  },
  [EQUIPMENT_NAMES.MAIN_EQUIPMENT]: MAIN_EQUIPMENT_INSTRUCTIONS,
  [EQUIPMENT_NAMES.LEVEL_OF_UTILIZATION]: {
    name: 'Utilization Levels',
    instruction:
      'Detail equipment utilization rates, focusing on efficiency and optimization. Provide data on usage patterns, downtime reduction strategies, and maintenance schedules.',
    exampleOne:
      'The company`s equipment operates at peak efficiency, with utilization rates consistently exceeding 90%. The company implements preventive maintenance schedules to minimize downtime and optimize performance. Continuous monitoring and data analysis drive efficiency improvements, ensuring smooth production processes.',
    exampleTwo:
      'The company`s equipment operates at peak efficiency, with utilization rates averaging at 90%. Downtime is minimized through proactive maintenance schedules, supported by predictive analytics. Usage patterns are constantly analyzed for optimization, ensuring maximum productivity and minimal disruptions.',
  },
  [EQUIPMENT_NAMES.PURCHASED_EQUIPMENT]: PURCHASED_EQUIPMENT_INSTRUCTIONS,
  [EQUIPMENT_NAMES.RATIONALE_FOR_PURCHASING]: {
    name: 'Purchase Rationale',
    instruction:
      'Justify equipment purchases by emphasizing efficiency gains and cost savings. Detail how the equipment aligns with production needs and supports business growth objectives.',
    exampleOne:
      'The company invests in cutting-edge equipment to enhance efficiency and reduce long-term costs. Upgrading ensures alignment with evolving production needs, boosting output and quality. This strategic investment accelerates the company`s growth trajectory, driving competitiveness in the market.',
    exampleTwo:
      'Investing in cutting-edge translation software and AI algorithms aligns with the company`s goal of enhancing efficiency and reducing operational costs. These tools streamline processes, ensuring timely delivery of high-quality translations while supporting scalability to meet growing demand.',
  },
};

const GROUPS_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction: 'Include a high-quality photo of the asset. Ensure the image is well-lit and professionally taken.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Name of the group',
    instruction: 'Provide the specific name of the asset group.',
    exampleOne: 'Service equipment',
    exampleTwo: 'Linguistic databases',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Number of items',
    instruction: 'Specify the total number of items in the asset group.',
    exampleOne: '9',
    exampleTwo: '3',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Current cost',
    instruction: 'State the current total value of the asset group.',
    exampleOne: '1 500 000 $',
    exampleTwo: '300 000 $',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Purpose',
    instruction:
      'Explain how the asset group contributes to the company`s operations, growth, or objectives. Describe its significance and benefits, including its impact on productivity, efficiency, profit generation, and growth strategy.',
    exampleOne:
      'These assets help maintain and repair the company`s tractors efficiently. They enable timely interventions, ensuring the tractors` longevity and optimal performance, which enhances customer satisfaction and loyalty.',
    exampleTwo:
      'These databases store linguistic data, including terminologies and phrases necessary for precise translation and localization efforts. Language professionals use them to bridge linguistic gaps between cultures and languages, ensuring clear communication and cultural sensitivity.',
  },
};

const PURCHASED_GROUPS_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction: 'Include a high-quality photo of the asset. Ensure the image is well-lit and professionally taken.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Name of the group',
    instruction: 'Provide the specific name of the asset group.',
    exampleOne: 'Satellite imaging systems',
    exampleTwo: 'AI-driven translation tools',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Number of items',
    instruction: 'Specify the total number of items in the asset group.',
    exampleOne: '3',
    exampleTwo: '2',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Planned cost',
    instruction: 'State the current total value of the asset group.',
    exampleOne: '1 000 000 $',
    exampleTwo: '100 000 $',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Purpose',
    instruction:
      'State the purpose of the proposed acquisition and specify the tasks or goals that will be achieved with these assets.',
    exampleOne:
      'Provide real-time data on field conditions, enabling precise monitoring of crop health, soil moisture levels, and overall land management for enhanced productivity and resource efficiency.',
    exampleTwo:
      'The platform enables faster and more reliable communication across multiple languages for clients and translators by improving the efficiency and accuracy of language translation processes.',
  },
};

const ASSETS_INSTRUCTIONS = {
  [ASSETS_NAMES.DESCRIPTION]: {
    name: 'Non-Current Assets',
    instruction:
      'Identify key non-current assets such as land, biological assets, transportation, and intangible assets. Provide concise descriptions for each category to ensure comprehensive coverage.',
    exampleOne: 'Trucks. 5 trucks supply the necessary parts and raw materials.',
    exampleTwo: 'Service cars. Managers use cars to travel to meetings with suppliers, investors or partners.',
  },
  [ASSETS_NAMES.GROUPS]: GROUPS_INSTRUCTIONS,
  [ASSETS_NAMES.PURCHASED_GROUPS]: PURCHASED_GROUPS_INSTRUCTIONS,
  [ASSETS_NAMES.RATIONALE_FOR_PURCHASING]: {
    name: 'Acquisition Justification',
    instruction:
      'Explain asset purchases` strategic importance, highlighting value addition and operational benefits. Detail how the assets contribute to efficiency, productivity, or competitive advantage in the business.',
    exampleOne:
      'Acquiring additional assets like advanced software enhances data analytics and streamlines operations, amplifying efficiency and decision-making. These investments bolster productivity and fortify the company`s competitive edge in the market, paving the path for sustained growth and innovation.',
    exampleTwo:
      'Acquiring additional assets such as high-speed servers and advanced data security systems enhances operational efficiency and fortifies the company`s competitive edge. These investments bolster data processing capabilities, ensuring seamless operations and safeguarding sensitive information, ultimately driving business growth.',
  },
};

const STAGES_INSTRUCTIONS = {
  [STAGES_NAMES.STAGE]: {
    name: 'Process Stages',
    instruction:
      'Outline key stages of the production process. Provide a step-by-step overview of product or service creation. Briefly describe each stage to offer insight into the technological process.',
    exampleOne:
      'Component Fabrication: Manufacture individual parts such as chassis, engine components, transmission systems, and track assemblies.',
    exampleTwo:
      'Request Submission: Clients submit translation requests, specifying language pairs, deadlines, and project details.',
  },
};

const TECHNOLOGICAL_PROCESS_INSTRUCTIONS = {
  [TECHNOLOGICAL_PROCESS_NAMES.TECHNOLOGY]: {
    name: 'Production Technology',
    instruction:
      'Outline the current technology employed in production or service provision. Emphasize efficiency gains, quality improvements, and competitive advantages.',
    exampleOne:
      'The current production technology utilizes patented nano-rubber technology. This innovation, applied to the Company`s continuous track design, minimizes soil pressure and impact, enhancing farming efficiency and providing a competitive edge.',
    exampleTwo:
      'The service provision leverages cutting-edge translation software powered by AI algorithms, ensuring unparalleled accuracy and efficiency. This technology enables the company to deliver high-quality translations promptly, maintaining a competitive edge in the market while enhancing client satisfaction.',
  },
  [TECHNOLOGICAL_PROCESS_NAMES.QUALITIES]: {
    name: 'Advantages/Disadvantages',
    instruction:
      'Detail the benefits of current technology, focusing on efficiency, quality, and competitive edge. Acknowledge limitations, such as cost, learning curve, or dependence on infrastructure.',
    exampleOne:
      'Technology allows for enhanced traction, reduced soil pressure, improved flotation, and increased farming efficiency. However, technology has higher production costs, lower wear resistance and limited application outside agriculture.',
    exampleTwo: '',
  },
  [TECHNOLOGICAL_PROCESS_NAMES.STAGES]: STAGES_INSTRUCTIONS,
  [TECHNOLOGICAL_PROCESS_NAMES.COMPETING_TECHNOLOGY]: {
    name: 'Competing Technologies',
    instruction:
      'Provide an overview of the broader technological landscape in your industry. Identify key technologies currently used by your competitors or emerging market. Discuss how these competing technologies could impact your product or service. Analyze its strengths and weaknesses.',
    exampleOne:
      'Competing technologies like autonomous vehicles and electric tractors pose potential threats. While offering advancements in efficiency and sustainability, they may challenge the Company`s traditional tractor market dominance due to high initial costs and adoption barriers.',
    exampleTwo:
      'Competing technologies such as machine translation, language processing APIs, and crowdsourcing platforms may impact the company`s market share. While they offer automation and scalability, they may need more precision and quality assurance of human translators.',
  },
};

const WORKING_RESOURCES_INSTRUCTIONS = {
  [WORKING_RESOURCES_NAMES.NECESSARY_RECOURSES]: {
    name: 'Material Requirements',
    instruction:
      'Present a concise overview of the essential resources needed for your production process. Delineate the primary categories of resources, such as raw materials, materials, and components. Outline these resources` critical role in the production lifecycle and emphasize their significance in ensuring a seamless manufacturing process.',
    exampleOne:
      'The Company needs rubber, plastics, and steel for production. Also included are components such as tracks, chassis, engines, transmissions, hydraulic cylinders, corps, cooling systems, operator`s stations, fuel, and electrical systems.',
    exampleTwo:
      'The necessary material resources include office supplies such as stationery, coffee, tea, water, and disposable tableware. These items are essential for maintaining a comfortable working environment for employees.',
  },
  [WORKING_RESOURCES_NAMES.AVAILABILITY]: {
    name: 'Resource Stability',
    instruction:
      'Detail resource availability and stability, emphasizing reliability and continuity. Discuss inventory plans for any potential disruptions to ensure uninterrupted operations.',
    exampleOne:
      'Resource supply is consistently monitored for stability and availability. Companies maintain diverse supplier networks to mitigate disruptions. Inventory plans include safety stocks and alternative sourcing strategies to ensure continuous operations amid potential disruptions.',
    exampleTwo:
      'Reliable suppliers and diversified sourcing ensure resource availability and stability. The Company`s supplier has demonstrated reliability over the past four years. Continuous monitoring of competitors ensures swift adjustments in case of unforeseen circumstances.',
  },
};

const KEY_SUPPLIERS_INSTRUCTIONS = {
  [KEY_SUPPLIERS_CHARACTERIZE.NAME]: {
    name: 'Supplier name',
    instruction: 'Provide the full name of the supplier, include any relevant details.',
    exampleOne: 'Steelnow',
    exampleTwo: 'VPS',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.PRODUCT]: {
    name: 'Service/product',
    instruction: 'Provide the name of the product or service supplied.',
    exampleOne: 'High-strength low-alloy steel',
    exampleTwo: 'Stationery: writing instruments, paper and pad, filing and storage',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.QUANTITY]: {
    name: 'Quantity per month',
    instruction:
      'Specify the monthly quantity of the product or service provided by the supplier, include relevant units.',
    exampleOne: '1,5 tn',
    exampleTwo: 'Total 50 units',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.PRICE]: {
    name: 'Price',
    instruction: 'Specify the price of the product or service offered by the supplier accurately.',
    exampleOne: '500 USD per ton',
    exampleTwo: '300 USD',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.DELIVERY_TERMS]: {
    name: 'Delivery terms',
    instruction:
      "Indicate delivery terms succinctly, highlighting whether it's prepayment, upon delivery, or deferred payment.",
    exampleOne: '',
    exampleTwo: '',
  },
};

const PARTNERS_INSTRUCTIONS = {
  [PARTNERS_NAMES.KEY_SUPPLIERS]: KEY_SUPPLIERS_INSTRUCTIONS,
  [PARTNERS_NAMES.CRITERIA]: {
    name: 'Selection Criteria',
    instruction:
      'Outline the specific criteria and considerations that will guide your supplier selection process. Discuss factors such as favourable pricing, convenient logistics, flexible credit terms, and the unique quality of raw materials. Clearly articulate the importance of each criterion in the context of your business strategy and objectives.',
    exampleOne:
      'When choosing a supplier, the Company considers the quality of the raw materials and components, which is crucial for tractor durability. Reliable logistics ensures the timely delivery of components and materials, which is of paramount importance for our production processes.',
    exampleTwo:
      'When choosing a supplier, the Company prioritizes competitive pricing for cost-effectiveness, a reliable system for uninterrupted service, and flexible lending terms for financial stability.',
  },
};

const PRODUCTION_INSTRUCTIONS = {
  [PRODUCTION_NAMES.SPECIFICS]: {
    name: 'Production Specifics',
    instruction:
      'Detail seasonal work schedules, addressing fluctuations in demand. Highlight plans for peak season optimization and adjustments in work schedules. Consider regulatory or external dependencies impacting production timelines.',
    exampleOne:
      'Production runs year-round; during peak seasons, such as spring and fall, the company implements extended work hours and additional shifts to meet increased demand. Schedules are adjusted accordingly, with flexibility to accommodate fluctuations.',
    exampleTwo:
      'The Company`s operations are tailored to meet seasonal demand, with peak periods aligning with academic semesters and international events. Flexible work schedules are implemented to accommodate these fluctuations.',
  },
  [PRODUCTION_NAMES.PACKAGE]: {
    name: 'Packaging',
    instruction:
      'Outline the packaging strategy, emphasizing product protection and brand image. Detail materials, design, and eco-friendly considerations if applicable.',
    exampleOne:
      'Depending on the method of transport, the tractors can be loaded onto platforms or into containers. They are secured with straps or chains to prevent movement during transport. Additional materials may be used to protect sensitive parts of the tractor from damage during transport.',
    exampleTwo:
      'The Company`s packaging strategy focuses on ensuring product protection. To minimize environmental impact, the Company uses durable and eco-friendly materials. Designs incorporate the Company`s brand colours and logos for recognition. ',
  },
  [PRODUCTION_NAMES.INVENTORY_MANAGEMENT]: {
    name: 'Inventory Management',
    instruction:
      'Provide an overview of the company`s inventory management system. Detail the software or program utilized for effective inventory control. Discuss the system`s critical features, such as real-time tracking, order management, and integration with other business processes.',
    exampleOne:
      'The Company uses ERP software for integrated inventory management, allowing real-time tracking of orders and integration with other business processes such as production and sales. The system ensures optimal inventory levels to meet demand and minimize storage costs.',
    exampleTwo:
      'The Company uses a cloud-based inventory management system, which enables real-time tracking of issues and seamless integration with other services to ensure smooth system operation.',
  },
  [PRODUCTION_NAMES.OUTSOURCING_OPPORTUNITIES]: {
    name: 'Outsourcing Options',
    instruction:
      'Provide a concise overview of the outsourcing opportunities available for your production processes. Discuss the specific stages or aspects of production that could be outsourced and the potential benefits of doing so. Highlight the strategic reasons behind outsourcing, such as cost efficiency, scalability, or access to specialized expertise.',
    exampleOne:
      'The Company outsources component manufacturing, assembly, and distribution logistics. Outsourcing offers cost-effectiveness, scalability, and access to specialized expertise.',
    exampleTwo:
      'The Company outsources customer support and content creation, allowing you to reduce the burden on the HR department and perform project tasks without registering an employee in the staff.',
  },
  [PRODUCTION_NAMES.INVOLVED_PRODUCTION]: {
    name: 'Third-Party Involvement',
    instruction:
      'State which intermediate stages or specific aspects of production are entrusted to external entities. Provide a brief rationale behind this decision, emphasizing the strategic advantages or efficiencies gained through collaboration with third parties.',
    exampleOne:
      'Collaboration with third-party companies is necessary to fabricate specialized components, such as hydraulic systems and engines.',
    exampleTwo:
      'Third-party services are used to ensure linguistic accuracy and cultural sensitivity in the production process. This collaboration improves quality, promotes diversity and speeds up project completion.',
  },
  [PRODUCTION_NAMES.DISPOSAL_WAIST]: {
    name: 'Waste Management',
    instruction:
      'Explain waste disposal procedures, emphasizing sustainability and compliance. Detail sorting methods, recycling efforts, and partnerships with disposal services.',
    exampleOne:
      'Onsite waste sorting is conducted for recycling or safe disposal. Recyclable materials, such as metal, plastic, and paper, are separated. Hazardous materials are disposed of through certified partners. Compliance and sustainability in waste management are ensured through continuous monitoring.',
    exampleTwo:
      'Waste electronic products are recycled according to international standards, ensuring sustainability and compliance. The Company partners with specialized disposal services to facilitate proper sorting and recycling and minimize environmental impact.',
  },
};

const QUALITY_INSTRUCTIONS = {
  [QUALITY_NAMES.FACTORS]: {
    name: 'Quality Factors',
    instruction:
      'Identify and elaborate on the critical factors that impact the quality of the final product. These may include raw materials, manufacturing processes, equipment precision, and adherence to industry standards.',
    exampleOne:
      'Critical factors impacting product quality include raw material quality and assembly accuracy. The quality of raw materials directly influences durability, while precise assembly ensures reliability and longevity.',
    exampleTwo:
      'Critical factors impacting product quality include accurate translation, cultural sensitivity, adherence to client specifications, and effective communication between translators and clients. Quality assurance processes and feedback mechanisms further ensure consistent, high-quality outcomes.',
  },
  [QUALITY_NAMES.METHODS]: {
    name: 'Control Methods',
    instruction:
      'Describe the primary quality control methods employed in the manufacturing or service delivery processes. Discuss any inspection protocols, testing procedures, or checkpoints integrated into the production cycle to ensure the consistency and reliability of the final output.',
    exampleOne:
      'Quality control methods include rigorous inspection of raw materials, regular testing of components, and comprehensive checks during assembly. Protocols for dimensional accuracy, strength, and performance are followed at each stage to ensure consistency and reliability.',
    exampleTwo:
      'Primary quality control methods include thorough document review, automated translation tools, and human proofreading. Regular audits, client feedback mechanisms, and performance evaluations uphold consistent quality standards throughout the service delivery.',
  },
};

export const BUSINESS_OPERATIONS_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.LOCATION_AND_PREMISES]: LOCATION_AND_PREMISES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.EQUIPMENT]: EQUIPMENT_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.ASSETS]: ASSETS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.TECHNOLOGICAL_PROCESS]: TECHNOLOGICAL_PROCESS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.WORKING_RESOURCES]: WORKING_RESOURCES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PARTNERS]: PARTNERS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PRODUCTION]: PRODUCTION_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.QUALITY]: QUALITY_INSTRUCTIONS,
};
